import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Clock, Shield, ThumbsUp, TrendingUp, Phone, MessageCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import LazyImage from '../components/LazyImage';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: <Award className="w-12 h-12" />,
      title: "15+ Years Experience",
      description: "Over a decade of expertise in pool construction and fitness solutions across Bengaluru"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Premium Quality Materials",
      description: "We use only the highest quality materials and equipment from trusted manufacturers"
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Expert Team",
      description: "Skilled professionals with extensive training and certifications"
    },
    {
      icon: <Clock className="w-12 h-12" />,
      title: "Timely Completion",
      description: "We respect your time and deliver projects on schedule, every time"
    },
    {
      icon: <ThumbsUp className="w-12 h-12" />,
      title: "Customer Satisfaction",
      description: "Our success is measured by your happiness and satisfaction"
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "After-Sales Support",
      description: "Comprehensive maintenance and support even after project completion"
    }
  ];

  const stats = [
    { number: "500+", label: "Projects Completed" },
    { number: "15+", label: "Years Experience" },
    { number: "450+", label: "Happy Clients" },
    { number: "100%", label: "Quality Assurance" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1749224725848-a450483cd75d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxwb29sJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc3MDA1MjIyNHww&ixlib=rb-4.1.0&q=85"
            alt="Why Choose Us"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Why Choose Aquarist?</h1>
          <p className="text-xl md:text-2xl">Your Trusted Pool & Fitness Partner</p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-aqua-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-5xl md:text-6xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg md:text-xl text-aqua-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reasons Section */}
      <section className="py-20 bg-gradient-to-b from-white to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Commitment to excellence in every aspect of our service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason, index) => (
              <Card 
                key={index}
                className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-aqua-100 to-aqua-200 text-aqua-600 mb-6">
                    {reason.icon}
                  </div>
                  <h3 className="text-2xl font-semibold text-navy-900 mb-4">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 text-lg">
                    {reason.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Our Quality Commitment</h2>
              <div className="space-y-4 text-lg text-gray-300">
                <p>
                  At Aquarist Pools and Fitness, quality isn't just a promise—it's our foundation. Every project we undertake reflects our unwavering commitment to excellence.
                </p>
                <p>
                  From the initial consultation to the final handover, we maintain the highest standards. Our team carefully selects materials, follows industry best practices, and ensures every detail meets our rigorous quality benchmarks.
                </p>
                <p>
                  We believe in building lasting relationships with our clients. That's why we stand behind our work with comprehensive warranties and ongoing support.
                </p>
                <p>
                  Your satisfaction is our success. We don't consider a project complete until you're absolutely delighted with the results.
                </p>
              </div>
            </div>
            <div className="relative">
              <LazyImage 
                src="https://images.unsplash.com/photo-1758448501014-3a6068fb9d51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwb29sJTIwZGVzaWdufGVufDB8fHx8MTc3MDA1MjE3NXww&ixlib=rb-4.1.0&q=85"
                alt="Quality Pool Construction"
                className="rounded-lg shadow-2xl aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600">Simple, transparent, and efficient</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "Discuss your vision and requirements" },
              { step: "02", title: "Design", desc: "Create custom plans tailored to your space" },
              { step: "03", title: "Construction", desc: "Expert execution with quality materials" },
              { step: "04", title: "Support", desc: "Ongoing maintenance and assistance" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-bold text-aqua-500 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-navy-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-[#003366] mb-2">Trusted & Certified</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-8 h-8" />, label: "15+ Years Certified Experience" },
              { icon: <Award className="w-8 h-8" />, label: "Authorised SOPREMAPOOL Dealer" },
              { icon: <CheckCircle className="w-8 h-8" />, label: "100% Quality Guaranteed" },
              { icon: <Users className="w-8 h-8" />, label: "500+ Projects Completed" }
            ].map((badge, i) => (
              <div key={i} className="flex flex-col items-center gap-3 bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="text-[#00AEEF]">{badge.icon}</div>
                <p className="text-sm font-semibold text-[#003366]">{badge.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="py-16 bg-gradient-to-r from-[#003366] to-[#001a33] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Get Your Free Pool Consultation</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8 text-lg">
            Speak to our pool construction experts today. Free site evaluation, transparent pricing, and expert design advice.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/917757999805?text=Hi%20Aquarist%20Pools%2C%20I%20would%20like%20a%20free%20pool%20consultation."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-8 py-4 rounded-xl transition-all text-base shadow-lg"
            >
              <MessageCircle className="w-5 h-5 fill-white" /> Chat on WhatsApp
            </a>
            <a
              href="tel:+917757999805"
              className="flex items-center gap-2 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-bold px-8 py-4 rounded-xl transition-all text-base shadow-lg"
            >
              <Phone className="w-5 h-5" /> Call +91 77579 99805
            </a>
            <Button asChild className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 font-bold px-8 py-4 text-base">
              <Link to="/booking">Request Free Quote <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
